import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
  booleanAttribute,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideRotateCcw, lucideRotateCw, lucideZoomIn, lucideZoomOut } from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { ImageCroppedEvent, ImageCropperComponent, ImageTransform } from 'ngx-image-cropper';
import { ImageCropData } from '../../services/voucher-product.service';

@Component({
  selector: 'app-image-cropper-modal',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    ImageCropperComponent,
    NgIcon,
    HlmButtonImports,
    HlmDialogImports,
    HlmSpinnerImports,
    HlmTypographyImports,
  ],
  providers: [provideIcons({ lucideRotateCcw, lucideRotateCw, lucideZoomIn, lucideZoomOut })],
  templateUrl: './image-cropper-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./image-cropper-modal.component.css'],
})
export class ImageCropperModalComponent implements OnInit, OnDestroy {
  @Input() imageFile: File | null = null;
  @Input() existingCropData: ImageCropData | null = null;
  @Input() roundCropper = false;
  @Input() aspectRatio = 1;
  @Input() cropDescription = '';
  @Input() resizeToWidth = 800;
  /** Skip the own dialog — for hosts that already are one. */
  @Input({ transform: booleanAttribute }) inline = false;
  @Output() cropComplete = new EventEmitter<{
    file: File;
    croppedFile: File;
    cropData: ImageCropData;
  }>();
  @Output() cancel = new EventEmitter<void>();

  /** The host renders only while open, so the dialog starts open. */
  open = true;

  /** Set when the picked file cannot be decoded — shown instead of the spinner. */
  loadError = false;
  ready = false;

  scale = 1;
  /** Degrees. Kept in this unit because the backend crop data is in degrees. */
  rotation = 0;
  transform: ImageTransform = {};
  cropPosition: { x: number; y: number; width: number; height: number } = {
    x: 0,
    y: 0,
    width: 800,
    height: 800,
  };

  /** The most recent crop result. Held as a Blob so saving needs no re-encode. */
  private croppedBlob: Blob | null = null;
  /** The library mints an object URL per crop and never frees it — we do. */
  private lastObjectUrl: string | null = null;

  /**
   * ngx-image-cropper counts canvas rotation in QUARTER TURNS, not degrees
   * (it applies `ctx.rotate(PI * canvasRotation / 2)`). Feeding it degrees made
   * the exported image rotate by a different amount than the preview showed.
   */
  get canvasRotation(): number {
    return (((this.rotation / 90) % 4) + 4) % 4;
  }

  get canSave(): boolean {
    return !!this.croppedBlob && !this.loadError;
  }

  ngOnInit() {
    // Restore existing crop settings if available
    if (this.existingCropData) {
      this.scale = this.existingCropData.zoom || 1;
      this.rotation = this.existingCropData.rotation || 0;
      this.cropPosition = {
        x: this.existingCropData.x || 0,
        y: this.existingCropData.y || 0,
        width: this.existingCropData.width || 800,
        height: this.existingCropData.height || 800,
      };

      this.updateTransform();
    }
  }

  ngOnDestroy() {
    this.releaseObjectUrl();
  }

  imageCropped(event: ImageCroppedEvent) {
    // Each crop allocates a fresh blob plus an object URL that keeps it alive.
    // Dragging the crop box fires this continuously, so release the previous one.
    this.releaseObjectUrl();
    this.lastObjectUrl = event.objectUrl ?? null;
    this.croppedBlob = event.blob ?? null;

    // Capture the actual crop position from the event
    if (event.cropperPosition) {
      this.cropPosition = {
        x: event.cropperPosition.x1,
        y: event.cropperPosition.y1,
        width: event.cropperPosition.x2 - event.cropperPosition.x1,
        height: event.cropperPosition.y2 - event.cropperPosition.y1,
      };
    }
  }

  cropperReady() {
    this.ready = true;
  }

  loadImageFailed() {
    // Used to alert() and leave the dialog sitting on a spinner forever.
    this.loadError = true;
    this.ready = false;
  }

  zoomIn() {
    this.scale = Math.min(this.scale + 0.1, 10);
    this.updateTransform();
  }

  zoomOut() {
    if (this.scale > 0.2) {
      this.scale = Math.round((this.scale - 0.1) * 100) / 100;
      this.updateTransform();
    }
  }

  rotateLeft() {
    this.rotation -= 90;
    this.updateTransform();
  }

  rotateRight() {
    this.rotation += 90;
    this.updateTransform();
  }

  resetTransform() {
    this.scale = 1;
    this.rotation = 0;
    this.updateTransform();
  }

  private updateTransform() {
    // `rotate` deliberately stays out of here: the canvas rotation above already
    // turns the image, and setting both rotated the preview twice over.
    this.transform = { scale: this.scale };
  }

  private releaseObjectUrl() {
    if (this.lastObjectUrl) {
      URL.revokeObjectURL(this.lastObjectUrl);
      this.lastObjectUrl = null;
    }
  }

  onSave() {
    if (!this.croppedBlob || !this.imageFile) return;

    const croppedFile = new File([this.croppedBlob], 'cropped-' + this.imageFile.name, {
      type: this.croppedBlob.type || 'image/jpeg',
    });

    const cropData: ImageCropData = {
      x: this.cropPosition.x,
      y: this.cropPosition.y,
      width: this.cropPosition.width,
      height: this.cropPosition.height,
      zoom: this.scale,
      rotation: this.rotation,
    };

    this.cropComplete.emit({
      file: this.imageFile, // Original file
      croppedFile, // Cropped file
      cropData,
    });
  }

  onCancel() {
    this.open = false;
    this.cancel.emit();
  }

  /** Escape or a click on the backdrop closes the dialog — treat it as cancel. */
  onDialogState(state: 'open' | 'closed') {
    if (state === 'closed' && this.open) {
      this.onCancel();
    }
  }
}
